// ════════════════════════════════════════════════════════════════
// ReportsScreen.jsx
// Reports screen — Flat Owner YTD Report + Tenant Report
// Accessible to: MC (all flats), Flat Owner (own flat), Tenant (own report)
// ════════════════════════════════════════════════════════════════
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import {
  collection, doc, getDoc, getDocs, query, where,
} from "firebase/firestore";
import {
  generateFlatOwnerReport,
  generateTenantReport,
  generateOutstandingDuesReport,
  generateIncomeExpenseReport,
  generateWaterConsumptionReport,
} from "../services/ReportService";

// ── Helpers ──────────────────────────────────────────────────────
function getFYMonths() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const fyStartYear = m >= 4 ? y : y - 1;
  const todayKey = `${y}-${String(m).padStart(2, "0")}`;
  const months = [];
  let cy = fyStartYear, cm = 4;
  while (true) {
    const key = `${cy}-${String(cm).padStart(2, "0")}`;
    if (key > todayKey) break;
    months.push(key);
    cm++;
    if (cm > 12) { cm = 1; cy++; }
  }
  return months;
}

// Fallback only — used if a flat is somehow missing its own sortOrder
// field in Firestore. The real, live ordering comes from each flat
// document's own "sortOrder" number (see sortFlatsByOrder below).
const DEFAULT_FLAT_ORDER = ["G1","G2","G3","101","102","103","201","202","203","301","302","303"];

// Sorts a list of flat records (each already containing its own Firestore
// fields, including "sortOrder") into display order.
//   1. If both flats have a sortOrder number, use that (the real, live
//      per-society order — works for any society, any flat count).
//   2. Otherwise, fall back to the old fixed BSKR list, for safety.
//   3. Otherwise, fall back to plain alphabetical, so nothing ever crashes.
function sortFlatsByOrder(flats, flatIdKey = "flatId") {
  return [...flats].sort((a, b) => {
    if (typeof a.sortOrder === "number" && typeof b.sortOrder === "number") {
      return a.sortOrder - b.sortOrder;
    }
    const ai = DEFAULT_FLAT_ORDER.indexOf(a[flatIdKey]);
    const bi = DEFAULT_FLAT_ORDER.indexOf(b[flatIdKey]);
    if (ai !== -1 && bi !== -1) return ai - bi;
    return (a[flatIdKey] || "").localeCompare(b[flatIdKey] || "");
  });
}

// ── Small UI helpers ─────────────────────────────────────────────
function SectionTitle({ text }) {
  return (
    <div style={{
      fontSize: "11px", color: "#8888a8", textTransform: "uppercase",
      letterSpacing: "0.5px", fontWeight: "600", marginBottom: "8px", marginTop: "16px",
    }}>
      {text}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #1e1e2e" }}>
      <span style={{ fontSize: "12px", color: "#8888a8" }}>{label}</span>
      <span style={{ fontSize: "12px", color: "#e8e8f0", fontWeight: "600" }}>{value || "—"}</span>
    </div>
  );
}

function ReportCard({ icon, title, desc, onGenerate, loading, disabled }) {
  return (
    <div style={{
      background: "#1a1a24", border: "1px solid #2e2e40",
      borderRadius: "14px", padding: "16px", marginBottom: "12px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
        <span style={{ fontSize: "24px" }}>{icon}</span>
        <div>
          <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0" }}>{title}</div>
          <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>{desc}</div>
        </div>
      </div>
      <button
        onClick={onGenerate}
        disabled={loading || disabled}
        style={{
          width: "100%", padding: "11px", borderRadius: "10px",
          background: loading ? "#2e2e40" : "#6c63ff",
          color: loading ? "#8888a8" : "#fff",
          border: "none", fontSize: "13px", fontWeight: "700",
          cursor: loading || disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {loading ? "Generating PDF…" : "📥 Download PDF"}
      </button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// FLAT OWNER / TENANT VIEW
// Shows their own flat's reports only
// ════════════════════════════════════════════════════════════════
function ResidentReports({ user, settings }) {
  const [flatData,      setFlatData]      = useState(null);
  const [payments,      setPayments]      = useState([]);
  const [waterBills,    setWaterBills]    = useState([]);
  const [adhocPayments, setAdhocPayments] = useState([]);
  const [meterReadings, setMeterReadings]  = useState([]);
  const [tenants,       setTenants]       = useState([]); // inactive tenants for this flat
  const [loading,       setLoading]       = useState(true);
  const [genLoading,    setGenLoading]    = useState(false);
  const [tenantGenIdx,  setTenantGenIdx]  = useState(null);
  const [msg,           setMsg]           = useState("");

  useEffect(() => {
    async function load() {
      try {
        // Flat details
        const flatSnap = await getDoc(doc(db, "flats", user.flatId));
        if (flatSnap.exists()) setFlatData({ flatId: user.flatId, ...flatSnap.data() });

        // All FY month payments for this flat
        const fyMonths = getFYMonths();
        const pSnap = await getDocs(query(
          collection(db, "payments"),
          where("societyId", "==", user.societyId),
          where("flatId", "==", user.flatId),
          where("month", "in", fyMonths)
        ));
        setPayments(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // All water bills for this flat
        const wSnap = await getDocs(query(
          collection(db, "waterBills"),
          where("societyId", "==", user.societyId),
          where("flatId", "==", user.flatId)
        ));
        setWaterBills(wSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Society work payments for this flat
        const aSnap = await getDocs(query(
          collection(db, "adhocPayments"),
          where("societyId", "==", user.societyId),
          where("flatId", "==", user.flatId)
        ));
        setAdhocPayments(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Meter readings for this flat
        const mSnap = await getDocs(query(
          collection(db, "meterReadings"),
          where("societyId", "==", user.societyId),
          where("flatId", "==", user.flatId)
        ));
        setMeterReadings(mSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Past tenants (inactive) for tenant reports
        const tSnap = await getDocs(query(
          collection(db, "users"),
          where("societyId",  "==", user.societyId),
          where("flatId",     "==", user.flatId),
          where("memberType", "==", "tenant"),
          where("active",     "==", false)
        ));
        setTenants(tSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      } catch (e) {
        console.error("ReportsScreen load error:", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [user.flatId]);

  async function handleFlatOwnerReport() {
    setGenLoading(true);
    setMsg("");
    try {
      await generateFlatOwnerReport({
        settings,
        flat: {
          flatId: user.flatId,
          ownerName: flatData?.ownerName || user.name,
          phone: flatData?.mobile || user.phone || "",
          email: flatData?.emailId || user.email || "",
          maintenanceRate: flatData?.maintenanceRate,
        },
        payments,
        waterBills,
        meterReadings,
        adhocPayments,
      });
      setMsg("✅ Report downloaded successfully!");
    } catch (e) {
      console.error(e);
      setMsg("⚠ Failed to generate report. Please try again.");
    } finally {
      setGenLoading(false);
    }
  }

  async function handleTenantReport(tenant, idx) {
    setTenantGenIdx(idx);
    setMsg("");
    try {
      // Load tenant-specific payments (during their stay)
      const tPaySnap = await getDocs(query(
        collection(db, "payments"),
        where("societyId", "==", user.societyId),
        where("flatId", "==", user.flatId)
      ));
      const tPayments = tPaySnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Pocket expenses submitted by tenant or their family members
      const tenantNames = [
        tenant.name,
        ...(tenant.familyMembers || []).map(m => m.name),
      ].filter(Boolean);
      const pocketSnaps = await Promise.all(
        tenantNames.map(n => getDocs(query(
          collection(db, "expenses"),
          where("societyId", "==", user.societyId),
          where("addedBy", "==", n),
          where("isPocket", "==", true)
        )))
      );
      const pocketExpenses = pocketSnaps.flatMap(s => s.docs.map(d => ({ id: d.id, ...d.data() })));

      await generateTenantReport({
        settings,
        flat: {
          flatId: user.flatId,
          ownerName: flatData?.ownerName || user.name,
          maintenanceRate: flatData?.maintenanceRate,
        },
        tenant: {
          name: tenant.name,
          phone: tenant.phone || tenant.mobile || "",
          email: tenant.email || "",
          entryDate: tenant.entryDate || "",
          exitDate: tenant.exitDate || "",
          paysMaintenance: tenant.paysMaintenance || false,
          paysWater: tenant.paysWater || false,
          familyMembers: tenant.familyMembers || [],
        },
        payments: tPayments,
        waterBills,
        meterReadings,
        adhocPayments,
        pocketExpenses,
      });
      setMsg("✅ Tenant report downloaded!");
    } catch (e) {
      console.error(e);
      setMsg("⚠ Failed to generate tenant report.");
    } finally {
      setTenantGenIdx(null);
    }
  }

  // Currently active tenant downloading their OWN statement (their own login) —
  // uses their own account's paysMaintenance / paysWater, so they only see
  // the sections they've actually agreed to pay.
  async function handleMyTenantReport() {
    setGenLoading(true);
    setMsg("");
    try {
      const tPaySnap = await getDocs(query(
        collection(db, "payments"),
        where("societyId", "==", user.societyId),
        where("flatId", "==", user.flatId)
      ));
      const tPayments = tPaySnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const tenantNames = [
        user.name,
        ...(user.familyMembers || []).map(m => m.name),
      ].filter(Boolean);
      const pocketSnaps = await Promise.all(
        tenantNames.map(n => getDocs(query(
          collection(db, "expenses"),
          where("societyId", "==", user.societyId),
          where("addedBy", "==", n),
          where("isPocket", "==", true)
        )))
      );
      const pocketExpenses = pocketSnaps.flatMap(s => s.docs.map(d => ({ id: d.id, ...d.data() })));

      await generateTenantReport({
        settings,
        flat: {
          flatId: user.flatId,
          ownerName: flatData?.ownerName || "",
          maintenanceRate: flatData?.maintenanceRate,
        },
        tenant: {
          name: user.name,
          phone: user.phone || user.mobile || "",
          email: user.email || "",
          entryDate: user.entryDate || "",
          exitDate: user.exitDate || "",
          paysMaintenance: user.paysMaintenance || false,
          paysWater: user.paysWater || false,
          familyMembers: user.familyMembers || [],
        },
        payments: tPayments,
        waterBills,
        meterReadings,
        adhocPayments,
        pocketExpenses,
      });
      setMsg("✅ Report downloaded successfully!");
    } catch (e) {
      console.error(e);
      setMsg("⚠ Failed to generate report. Please try again.");
    } finally {
      setGenLoading(false);
    }
  }

  if (loading) {
    return <div style={{ textAlign: "center", padding: "60px 20px", color: "#555570", fontSize: "13px" }}>Loading reports…</div>;
  }

  // Is the person logged in right now the tenant, or the flat owner?
  const isTenant = user.memberType === "tenant";

  const now = new Date();
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;

  return (
    <div style={{ padding: "16px", maxWidth: "640px", margin: "0 auto" }}>
      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>📄 Reports</div>
        <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>
          Flat {user.flatId} · Download PDF statements
        </div>
      </div>

      {/* Flat Owner YTD Report — owners see this */}
      {!isTenant && (
        <>
          <SectionTitle text="Flat Owner Statement" />
          <ReportCard
            icon="🏠"
            title={`FY ${fyStartYear}-${String(fyStartYear + 1).slice(-2)} Statement`}
            desc="YTD — April to current month. Maintenance, Water, Meter Readings & Society Work."
            onGenerate={handleFlatOwnerReport}
            loading={genLoading}
          />
        </>
      )}

      {/* Your Statement — currently active tenants see this instead, limited to what they pay */}
      {isTenant && (
        <>
          <SectionTitle text="Your Statement" />
          <ReportCard
            icon="🧑‍💼"
            title={`FY ${fyStartYear}-${String(fyStartYear + 1).slice(-2)} Statement`}
            desc={`YTD — April to current month. ${user.paysMaintenance ? "Maintenance" : ""}${user.paysMaintenance && user.paysWater ? " & " : ""}${user.paysWater ? "Water" : ""}${!user.paysMaintenance && !user.paysWater ? "No dues assigned to you." : ""}`}
            onGenerate={handleMyTenantReport}
            loading={genLoading}
          />
        </>
      )}

      {/* Tenant Reports — one per past tenant */}
      {tenants.length > 0 && (
        <>
          <SectionTitle text="Tenant Reports" />
          {tenants.map((t, idx) => (
            <ReportCard
              key={t.id}
              icon="🧑‍💼"
              title={`Tenant: ${t.name}`}
              desc={`Stay: ${t.entryDate || "?"} to ${t.exitDate || "Present"}`}
              onGenerate={() => handleTenantReport(t, idx)}
              loading={tenantGenIdx === idx}
            />
          ))}
        </>
      )}

      {/* Status message */}
      {msg && (
        <div style={{
          marginTop: "12px", padding: "10px 14px", borderRadius: "10px",
          background: msg.startsWith("✅") ? "#4caf5018" : "#ff525218",
          border: `1px solid ${msg.startsWith("✅") ? "#4caf5040" : "#ff525240"}`,
          color: msg.startsWith("✅") ? "#4caf50" : "#ff5252",
          fontSize: "13px",
        }}>
          {msg}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MC VIEW
// Shows flat selector — generate report for any flat
// ════════════════════════════════════════════════════════════════
function MCReports({ user, settings }) {
  const [flats,         setFlats]         = useState([]);
  const [selectedFlat,  setSelectedFlat]  = useState("");
  const [flatData,      setFlatData]      = useState(null);
  const [payments,      setPayments]      = useState([]);
  const [waterBills,    setWaterBills]    = useState([]);
  const [adhocPayments, setAdhocPayments] = useState([]);
  const [meterReadings, setMeterReadings]  = useState([]);
  const [tenants,       setTenants]       = useState([]);
  const [activeTenant,  setActiveTenant]  = useState(null); // currently living in the flat, if rented out
  const [loading,       setLoading]       = useState(true);
  const [flatLoading,   setFlatLoading]   = useState(false);
  const [genLoading,    setGenLoading]    = useState(false);
  const [tenantGenIdx,  setTenantGenIdx]  = useState(null);
  const [msg,           setMsg]           = useState("");
  const [societyLoading, setSocietyLoading] = useState(false);

  // Load all flats list
  useEffect(() => {
    async function load() {
      try {
        const snap = await getDocs(query(collection(db, "flats"), where("societyId", "==", user.societyId)));
        const data = snap.docs.map(d => ({ flatId: d.id, ...d.data() }));
        setFlats(sortFlatsByOrder(data));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Load selected flat data
  useEffect(() => {
    if (!selectedFlat) return;
    async function loadFlat() {
      setFlatLoading(true);
      setMsg("");
      try {
        const flatSnap = await getDoc(doc(db, "flats", selectedFlat));
        if (flatSnap.exists()) setFlatData({ flatId: selectedFlat, ...flatSnap.data() });

        const fyMonths = getFYMonths();
        const pSnap = await getDocs(query(
          collection(db, "payments"),
          where("societyId", "==", user.societyId),
          where("flatId", "==", selectedFlat),
          where("month", "in", fyMonths)
        ));
        setPayments(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const wSnap = await getDocs(query(
          collection(db, "waterBills"),
          where("societyId", "==", user.societyId),
          where("flatId", "==", selectedFlat)
        ));
        setWaterBills(wSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const aSnap = await getDocs(query(
          collection(db, "adhocPayments"),
          where("societyId", "==", user.societyId),
          where("flatId", "==", selectedFlat)
        ));
        setAdhocPayments(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const mSnap = await getDocs(query(
          collection(db, "meterReadings"),
          where("societyId", "==", user.societyId),
          where("flatId", "==", selectedFlat)
        ));
        setMeterReadings(mSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const tSnap = await getDocs(query(
          collection(db, "users"),
          where("societyId",  "==", user.societyId),
          where("flatId",     "==", selectedFlat),
          where("memberType", "==", "tenant"),
          where("active",     "==", false)
        ));
        setTenants(tSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Currently active tenant (if this flat is rented out right now)
        const activeTSnap = await getDocs(query(
          collection(db, "users"),
          where("societyId",  "==", user.societyId),
          where("flatId",     "==", selectedFlat),
          where("memberType", "==", "tenant"),
          where("active",     "==", true)
        ));
        setActiveTenant(activeTSnap.empty ? null : { id: activeTSnap.docs[0].id, ...activeTSnap.docs[0].data() });
      } catch (e) {
        console.error(e);
      } finally {
        setFlatLoading(false);
      }
    }
    loadFlat();
  }, [selectedFlat]);

  // Helper — fetch all society data across all flats
  async function fetchSocietyData() {
    const fyMonths = getFYMonths();
    const [allFlatsSnap, allPaySnap, allWaterSnap, allMeterSnap, allExpSnap, allMaintIncSnap] = await Promise.all([
      getDocs(query(collection(db, "flats"), where("societyId", "==", user.societyId))),
      getDocs(query(collection(db, "payments"), where("societyId", "==", user.societyId), where("month", "in", fyMonths))),
      getDocs(query(collection(db, "waterBills"), where("societyId", "==", user.societyId))),
      getDocs(query(collection(db, "meterReadings"), where("societyId", "==", user.societyId))),
      getDocs(query(collection(db, "expenses"), where("societyId", "==", user.societyId))),
      getDocs(query(collection(db, "maintenanceIncome"), where("societyId", "==", user.societyId))),
    ]);
    const allFlats = sortFlatsByOrder(allFlatsSnap.docs.map(d => ({ flatId: d.id, ...d.data() })));
    return {
      allFlats,
      allPayments:       allPaySnap.docs.map(d => ({ id: d.id, ...d.data() })),
      allWaterBills:     allWaterSnap.docs.map(d => ({ id: d.id, ...d.data() })),
      allMeterReadings:  allMeterSnap.docs.map(d => ({ id: d.id, ...d.data() })),
      allExpenses:       allExpSnap.docs.map(d => ({ id: d.id, ...d.data() })),
      maintenanceIncome: allMaintIncSnap.docs.map(d => ({ id: d.id, ...d.data() })),
    };
  }

  async function handleOutstandingDuesReport() {
    setSocietyLoading("dues");
    setMsg("");
    try {
      const { allFlats, allPayments, allWaterBills } = await fetchSocietyData();
      await generateOutstandingDuesReport({ settings, flats: allFlats, allPayments, allWaterBills });
      setMsg("✅ Outstanding Dues report downloaded!");
    } catch (e) {
      console.error(e);
      setMsg("⚠ Failed to generate report.");
    } finally {
      setSocietyLoading(false);
    }
  }

  async function handleIncomeExpenseReport() {
    setSocietyLoading("income");
    setMsg("");
    try {
      const { allFlats, allPayments, allWaterBills, allExpenses, maintenanceIncome } = await fetchSocietyData();
      await generateIncomeExpenseReport({ settings, flats: allFlats, allPayments, allWaterBills, allExpenses, maintenanceIncome });
      setMsg("✅ Income & Expense report downloaded!");
    } catch (e) {
      console.error(e);
      setMsg("⚠ Failed to generate report.");
    } finally {
      setSocietyLoading(false);
    }
  }

  async function handleWaterConsumptionReport() {
    setSocietyLoading("water");
    setMsg("");
    try {
      const { allFlats, allWaterBills, allMeterReadings } = await fetchSocietyData();
      await generateWaterConsumptionReport({ settings, flats: allFlats, allWaterBills, allMeterReadings });
      setMsg("✅ Water Consumption report downloaded!");
    } catch (e) {
      console.error(e);
      setMsg("⚠ Failed to generate report.");
    } finally {
      setSocietyLoading(false);
    }
  }

  async function handleFlatOwnerReport() {
    setGenLoading(true);
    setMsg("");
    try {
      await generateFlatOwnerReport({
        settings,
        flat: {
          flatId: selectedFlat,
          ownerName: flatData?.ownerName || "",
          phone: flatData?.mobile || "",
          email: flatData?.emailId || "",
          maintenanceRate: flatData?.maintenanceRate,
        },
        payments,
        waterBills,
        meterReadings,
        adhocPayments,
      });
      setMsg("✅ Report downloaded successfully!");
    } catch (e) {
      console.error(e);
      setMsg("⚠ Failed to generate report. Please try again.");
    } finally {
      setGenLoading(false);
    }
  }

  async function handleTenantReport(tenant, idx) {
    setTenantGenIdx(idx);
    setMsg("");
    try {
      const tPaySnap = await getDocs(query(
        collection(db, "payments"),
        where("societyId", "==", user.societyId),
        where("flatId", "==", selectedFlat)
      ));
      const tPayments = tPaySnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Pocket expenses submitted by tenant or their family members
      const tenantNames = [
        tenant.name,
        ...(tenant.familyMembers || []).map(m => m.name),
      ].filter(Boolean);
      const pocketSnaps = await Promise.all(
        tenantNames.map(n => getDocs(query(
          collection(db, "expenses"),
          where("societyId", "==", user.societyId),
          where("addedBy", "==", n),
          where("isPocket", "==", true)
        )))
      );
      const pocketExpenses = pocketSnaps.flatMap(s => s.docs.map(d => ({ id: d.id, ...d.data() })));

      await generateTenantReport({
        settings,
        flat: {
          flatId: selectedFlat,
          ownerName: flatData?.ownerName || "",
          maintenanceRate: flatData?.maintenanceRate,
        },
        tenant: {
          name: tenant.name,
          phone: tenant.phone || tenant.mobile || "",
          email: tenant.email || "",
          entryDate: tenant.entryDate || "",
          exitDate: tenant.exitDate || "",
          paysMaintenance: tenant.paysMaintenance || false,
          paysWater: tenant.paysWater || false,
          familyMembers: tenant.familyMembers || [],
        },
        payments: tPayments,
        waterBills,
        meterReadings,
        adhocPayments,
        pocketExpenses,
      });
      setMsg("✅ Tenant report downloaded!");
    } catch (e) {
      console.error(e);
      setMsg("⚠ Failed to generate tenant report.");
    } finally {
      setTenantGenIdx(null);
    }
  }

  if (loading) {
    return <div style={{ textAlign: "center", padding: "60px 20px", color: "#555570", fontSize: "13px" }}>Loading…</div>;
  }

  const now = new Date();
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;

  return (
    <div style={{ padding: "16px", maxWidth: "640px", margin: "0 auto" }}>
      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>📄 Reports</div>
        <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>
          Generate PDF statements for any flat
        </div>
      </div>

      {/* Society-level Reports */}
      <SectionTitle text="Society Reports" />
      <ReportCard
        icon="📊"
        title="Outstanding Dues Report"
        desc="All flats — maintenance and water outstanding as of today."
        onGenerate={handleOutstandingDuesReport}
        loading={societyLoading === "dues"}
      />
      <ReportCard
        icon="💰"
        title="Income & Expense Statement"
        desc={`FY ${fyStartYear}-${String(fyStartYear + 1).slice(-2)} — total income collected vs expenses by category.`}
        onGenerate={handleIncomeExpenseReport}
        loading={societyLoading === "income"}
      />
      <ReportCard
        icon="💧"
        title="Water Consumption Report"
        desc="All flats — month-wise units consumed and bill amounts."
        onGenerate={handleWaterConsumptionReport}
        loading={societyLoading === "water"}
      />

      {/* Flat selector */}
      <SectionTitle text="Select Flat" />
      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "16px" }}>
        {flats.map(f => (
          <button
            key={f.flatId}
            onClick={() => setSelectedFlat(f.flatId)}
            style={{
              padding: "8px 14px", borderRadius: "10px", border: "1px solid",
              borderColor: selectedFlat === f.flatId ? "#6c63ff" : "#2e2e40",
              background: selectedFlat === f.flatId ? "#6c63ff20" : "#1a1a24",
              color: selectedFlat === f.flatId ? "#6c63ff" : "#8888a8",
              fontSize: "13px", fontWeight: "600", cursor: "pointer",
            }}
          >
            {f.flatId}
          </button>
        ))}
      </div>

      {/* Flat details + reports */}
      {selectedFlat && (
        flatLoading ? (
          <div style={{ textAlign: "center", padding: "30px", color: "#555570", fontSize: "13px" }}>Loading flat data…</div>
        ) : (
          <>
            {/* Flat info */}
            <div style={{ background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px", padding: "14px", marginBottom: "12px" }}>
              <InfoRow label="Owner" value={flatData?.ownerName} />
              {activeTenant && (
                <InfoRow label="Tenant" value={activeTenant.name} />
              )}
            </div>

            {/* Flat Owner YTD Report */}
            <SectionTitle text="Flat Owner Statement" />
            <ReportCard
              icon="🏠"
              title={`FY ${fyStartYear}-${String(fyStartYear + 1).slice(-2)} Statement`}
              desc="YTD — April to current month. Maintenance, Water, Meter Readings & Society Work."
              onGenerate={handleFlatOwnerReport}
              loading={genLoading}
            />

            {/* Tenant Reports */}
            {tenants.length > 0 && (
              <>
                <SectionTitle text="Tenant Reports" />
                {tenants.map((t, idx) => (
                  <ReportCard
                    key={t.id}
                    icon="🧑‍💼"
                    title={`Tenant: ${t.name}`}
                    desc={`Stay: ${t.entryDate || "?"} to ${t.exitDate || "Present"}`}
                    onGenerate={() => handleTenantReport(t, idx)}
                    loading={tenantGenIdx === idx}
                  />
                ))}
              </>
            )}
          </>
        )
      )}

      {/* Status message */}
      {msg && (
        <div style={{
          marginTop: "12px", padding: "10px 14px", borderRadius: "10px",
          background: msg.startsWith("✅") ? "#4caf5018" : "#ff525218",
          border: `1px solid ${msg.startsWith("✅") ? "#4caf5040" : "#ff525240"}`,
          color: msg.startsWith("✅") ? "#4caf50" : "#ff5252",
          fontSize: "13px",
        }}>
          {msg}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MAIN EXPORT — ReportsScreen
// ════════════════════════════════════════════════════════════════
export default function ReportsScreen() {
  const { user, isMC } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    async function load() {
      try {
        // settings/init holds the general society settings; the letterhead
        // image lives separately in settings/letterhead (moved there since
        // it's only needed here for PDF generation, not on every screen).
        const [initSnap, letterheadSnap] = await Promise.all([
          getDoc(doc(db, "settings", "init")),
          getDoc(doc(db, "settings", "letterhead")),
        ]);
        const initData = initSnap.exists() ? initSnap.data() : {};
        const letterheadImage = letterheadSnap.exists() ? letterheadSnap.data()?.image : null;
        setSettings({ ...initData, letterheadImage });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return <div style={{ textAlign: "center", padding: "60px 20px", color: "#555570", fontSize: "13px" }}>Loading…</div>;
  }

  if (isMC()) {
    return <MCReports user={user} settings={settings} />;
  }

  return <ResidentReports user={user} settings={settings} />;
}
